import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCostReport, type CostReport } from '../lib/ai-quality-api';
import SpotlightCard from '../components/react-bits/SpotlightCard/SpotlightCard';
import styles from './Analytics.module.css';

const DAY_MS = 86_400_000;

/** Small amounts need more places: a reply often costs a fraction of a cent. */
function usd(n: number | null): string {
  if (n === null) return '—';
  if (n === 0) return '$0';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: n < 1 ? 4 : 2 })}`;
}
const pct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 100)}%`);

// Cost per resolution for the selected site and range: measured AI spend from
// the usage ledger, priced from the model price table, divided over chats that
// ended resolved (insights/cost.ts on the server).
export function CostPanel({ siteId, days, isSuperAdmin }: { siteId: string; days: number; isSuperAdmin: boolean }) {
  const [report, setReport] = useState<CostReport | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ignore = false;
    setError(false);
    const to = new Date();
    const from = new Date(to.getTime() - days * DAY_MS);
    getCostReport(siteId, from.toISOString(), to.toISOString())
      .then((r) => {
        if (!ignore) setReport(r);
      })
      .catch(() => {
        if (!ignore) setError(true);
      });
    return () => {
      ignore = true;
    };
  }, [siteId, days]);

  return (
    <SpotlightCard spotlightColor="rgba(255, 107, 26, 0.12)" className={styles.panel}>
      <div className={styles.panelHead}>
        <p className={styles.panelTitle}>Cost per resolution</p>
        {isSuperAdmin && (
          <Link className={styles.drillLink} to="/admin/system">
            Edit prices →
          </Link>
        )}
      </div>
      {error ? (
        <p className={styles.drillText}>Couldn’t load the cost report.</p>
      ) : !report ? (
        <p className={styles.drillText}>Loading…</p>
      ) : (
        <>
          <ul className={styles.list} aria-label="Cost summary">
            <Row label="AI cost per resolution" value={usd(report.perResolution.aiCostPerResolution)} />
            <Row label="AI cost per AI-resolved chat" value={usd(report.perResolution.aiCostPerAiResolution)} />
            <Row
              label="Blended cost per resolution (AI + agent time)"
              value={
                report.agentCost === null ? 'set an agent hourly cost' : usd(report.perResolution.blendedCostPerResolution)
              }
            />
            <Row label="AI resolution rate" value={pct(report.aiResolutionRate)} />
            <Row label="AI escalation rate" value={`${pct(report.aiEscalationRate)} (${report.aiEscalations} / ${report.aiAssistedConversations} AI-assisted chats)`} />
            <Row label="Intent confidence (low / medium / high / unknown)" value={`${report.intentConfidenceDistribution.low} / ${report.intentConfidenceDistribution.medium} / ${report.intentConfidenceDistribution.high} / ${report.intentConfidenceDistribution.unknown}`} />
            <Row label="AI spend on conversations" value={usd(report.conversationSpend)} />
            <Row label="AI spend on indexing & evaluation" value={usd(report.overheadSpend)} />
            <Row label="Agent hours" value={report.agentHours.toFixed(1)} />
            <Row label="Knowledge-base searches" value={report.retrievals.toLocaleString()} />
            <Row label="AI action calls" value={(report.toolCalls ?? 0).toLocaleString()} />
            <Row
              label="Chats ended (AI / human / ticketed / abandoned)"
              value={`${report.byOutcome.ai_resolved} / ${report.byOutcome.human_resolved} / ${report.byOutcome.ticketed} / ${report.byOutcome.abandoned}`}
            />
          </ul>

          <p className={styles.drillText}>Escalation counts ended AI-assisted chats that requested a person or received a human reply. Intent confidence comes from the trained intent classifier: low &lt;50%, medium 50–79%, high ≥80%; unknown means no classifier score. It does not measure answer accuracy.</p>
          {(report.unpricedCalls > 0 || report.unknownUsageCalls > 0 || report.pendingOutcomes > 0) && (
            <p className={styles.drillText}>
              {[
                report.unpricedCalls > 0 &&
                  `${report.unpricedCalls.toLocaleString()} calls on models without a price are left out.`,
                report.unknownUsageCalls > 0 &&
                  `${report.unknownUsageCalls.toLocaleString()} calls reported no token counts (cost unknown, not zero).`,
                report.pendingOutcomes > 0 && `${report.pendingOutcomes} ended chats are still being scored.`,
              ]
                .filter(Boolean)
                .join(' ')}
            </p>
          )}

          {report.byIntent.length > 0 && (
            <table className={styles.table} aria-label="Cost by intent">
              <thead>
                <tr>
                  <th>Intent</th>
                  <th className={styles.numCol}>Chats</th>
                  <th className={styles.numCol}>AI resolved</th>
                  <th className={styles.numCol}>Human resolved</th>
                  <th className={styles.numCol}>AI spend</th>
                </tr>
              </thead>
              <tbody>
                {report.byIntent.map((i) => (
                  <tr key={i.key ?? '∅'}>
                    <td>{i.name}</td>
                    <td className={styles.numCol}>{i.conversations}</td>
                    <td className={styles.numCol}>{i.aiResolved}</td>
                    <td className={styles.numCol}>{i.humanResolved}</td>
                    <td className={styles.numCol}>{usd(i.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {report.byModel.length > 0 && (
            <table className={styles.table} aria-label="Cost by model">
              <thead>
                <tr>
                  <th>Model</th>
                  <th className={styles.numCol}>Calls</th>
                  <th className={styles.numCol}>Input tokens</th>
                  <th className={styles.numCol}>Output tokens</th>
                  <th className={styles.numCol}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {report.byModel.map((m) => (
                  <tr key={`${m.provider}/${m.model}`}>
                    <td>
                      {m.provider} / {m.model}
                    </td>
                    <td className={styles.numCol}>{m.calls.toLocaleString()}</td>
                    <td className={styles.numCol}>{m.inputTokens.toLocaleString()}</td>
                    <td className={styles.numCol}>{m.outputTokens.toLocaleString()}</td>
                    <td className={styles.numCol}>{m.cost === null ? 'no price' : usd(m.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {report.conversations === 0 && <p className={styles.drillText}>No chats ended in this range.</p>}
        </>
      )}
    </SpotlightCard>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <li className={styles.listRow}>
      <span className={styles.listLabel}>{label}</span>
      <span className={styles.listValue}>{value}</span>
    </li>
  );
}
